<template>
  <div class="finance-view">
    <FinanceTopBar
      :park="park"
      :loading="loading"
      :periods="periods"
      :period="selectedPeriod"
      :comparison-mode="comparisonMode"
      :comparison-period="comparisonPeriod"
      :loading-summary="loadingSummary"
      @update:period="selectedPeriod = $event"
      @update:comparisonMode="onToggleComparisonMode"
      @update:comparisonPeriod="comparisonPeriod = $event"
      @generate-report="onGenerateReport"
    />

    <div v-if="loading" class="finance-loading">
      <span class="mdi mdi-loading finance-loading__spinner"></span>
      <span class="finance-loading__text">Cargando finanzas...</span>
    </div>

    <template v-else>
      <main class="finance-content">
        <div v-if="comparisonMode" class="finance-comparison-banner">
          <span>Comparando {{ periodLabel(selectedPeriod) }} con {{ periodLabel(comparisonPeriod) }}</span>
          <button @click="comparisonMode = false">Salir</button>
        </div>

        <div class="finance-kpi-row" v-if="summary">
          <NoiCard
            :park-id="park?.id"
            :period-key="selectedPeriod"
            :period="summary.period"
            :previous-period="summary.previousPeriod"
            :previous-period-label="periodLabel(summary.previousPeriod?.periodKey)"
            :year-to-date-n-o-i="summary.yearToDateNOI"
            :expenses-breakdown="summary.expensesBreakdown"
            :comparison-mode="comparisonMode"
            :comparison-snapshot="summary.comparison?.snapshot"
            :period-a-label="periodLabel(selectedPeriod)"
            :period-b-label="periodLabel(comparisonPeriod)"
          />
          <MarginCard
            :period="summary.period"
            :previous-period-label="periodLabel(summary.previousPeriod?.periodKey)"
            :margin-trend-points="summary.comparison?.diffs?.marginPoints ?? summary.marginTrendPoints"
            :twelve-month-avg-margin="summary.twelveMonthAvgMargin"
            :margin-history="summary.marginHistory"
            :comparison-mode="comparisonMode"
            :comparison-snapshot="summary.comparison?.snapshot"
            :period-a-label="periodLabel(selectedPeriod)"
            :period-b-label="periodLabel(comparisonPeriod)"
          />
        </div>

        <div class="finance-bottom-toggle">
          <button
            class="finance-bottom-toggle__btn"
            :class="{ 'finance-bottom-toggle__btn--active': bottomView === 'history' }"
            @click="bottomView = 'history'"
          >
            Histórico
          </button>
          <button
            class="finance-bottom-toggle__btn"
            :class="{ 'finance-bottom-toggle__btn--active': bottomView === 'projection' }"
            @click="bottomView = 'projection'"
          >
            Proyección y riesgo
          </button>
        </div>

        <FinanceHistoryChart
          v-if="bottomView === 'history' && history.seriesA.length"
          class="finance-chart"
          :series-a="history.seriesA"
          :series-b="history.seriesB"
          :comparison-mode="comparisonMode"
          :range="chartRange"
          :anchor-year="history.anchorYear"
          :anchor-month="history.anchorMonth"
          :period-type="history.periodType"
          :expenses-breakdown="summary?.expensesBreakdown"
          :park-id="park?.id"
          @update:range="onChangeRange"
          @refresh="onExpensesRefresh"
        />

        <FinanceProjectionRiskCard
          v-if="bottomView === 'projection' && projection"
          class="finance-projection"
          :months="projection.months"
          :projected-total-sum="projection.projectedTotalSum"
          :risk-total-sum="projection.riskTotalSum"
          :risk-ratio="projection.riskRatio"
          :needs-attention="projection.needsAttention"
        />
      </main>
    </template>
  </div>
  <ReportPanel v-if="showReportPanel" :park-id="park?.id" :has-logo="!!park?.logoUrl" @close="showReportPanel = false" />
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import FinanceTopBar from '@/components/Finanzas/FinanceTopBar.vue'
import NoiCard from '@/components/Finanzas/NoiCard.vue'
import MarginCard from '@/components/Finanzas/MarginCard.vue'
import FinanceHistoryChart from '@/components/Finanzas/FinanceHistoryChart.vue'
import FinanceProjectionRiskCard from '@/components/Finanzas/FinanceProjectionRiskCard.vue'
import ReportPanel from '@/components/Finanzas/ReportPanel.vue'
import {
  getParks,
  getParkById,
  getFinancePeriods,
  getFinanceSummary,
  getFinanceHistory,
  getFinanceProjection,
} from '@/services/parks'

// --- Estado ---
// Qué se muestra debajo de los KPIs: la gráfica histórica o la proyección+riesgo.
// Antes se mostraban ambas apiladas y la página quedaba con scroll; ahora es un toggle.
const bottomView = ref('history')
const park = ref(null)
const loading = ref(true)
const periods = ref({ months: [], quarters: [], currentYear: null })
const selectedPeriod = ref('')
const comparisonMode = ref(false)
const comparisonPeriod = ref('')
const chartRange = ref(12)

const summary = ref(null)
const loadingSummary = ref(false)
const history = ref({ seriesA: [], seriesB: null, anchorYear: null, anchorMonth: null, periodType: 'MONTH' })
const projection = ref(null)

const showReportPanel = ref(false)

// --- Carga de datos ---
async function loadSummary() {
  if (!park.value || !selectedPeriod.value) return
  loadingSummary.value = true
  try {
    summary.value = await getFinanceSummary(
      park.value.id,
      selectedPeriod.value,
      comparisonMode.value ? comparisonPeriod.value : undefined
    )
  } finally {
    loadingSummary.value = false
  }
}

async function loadHistory() {
  if (!park.value || !selectedPeriod.value) return
  history.value = await getFinanceHistory(
    park.value.id,
    selectedPeriod.value,
    comparisonMode.value ? comparisonPeriod.value : undefined,
    chartRange.value
  )
}

async function loadProjection() {
  if (!park.value) return
  projection.value = await getFinanceProjection(park.value.id)
}

function periodLabel(value) {
  if (!value) return ''
  const all = [...periods.value.months, ...periods.value.quarters, ...(periods.value.currentYear ? [periods.value.currentYear] : [])]
  const found = all.find((p) => p.value === value)
  return found?.label || value
}

function onChangeRange(newRange) {
  chartRange.value = newRange
  loadHistory()
}

function onToggleComparisonMode(value) {
  comparisonMode.value = value
  if (value && !comparisonPeriod.value) {
    // Por defecto, compara contra el periodo anterior al seleccionado
    comparisonPeriod.value = summary.value?.previousPeriod?.periodKey || selectedPeriod.value
  }
}

async function onExpensesRefresh() {
  await Promise.all([loadSummary(), loadHistory()])
}

function onGenerateReport() {
  showReportPanel.value = true
}

// --- Watchers ---
watch([selectedPeriod, comparisonMode, comparisonPeriod], () => {
  loadSummary()
  loadHistory()
})

// --- Carga inicial ---
onMounted(async () => {
  try {
    const parks = await getParks()
    if (parks[0]) {
      const [fullPark, periodsData] = await Promise.all([
        getParkById(parks[0].id),
        getFinancePeriods(parks[0].id),
      ])
      park.value = fullPark
      periods.value = periodsData
      selectedPeriod.value = periodsData.months[0]?.value || periodsData.currentYear?.value || ''
      await Promise.all([loadSummary(), loadHistory(), loadProjection()])
    }
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.finance-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.finance-content {
  flex: 1;
  min-height: 0;
  padding: 1.5rem 2rem;
  color: var(--text-primary);
  font-family: var(--font-body);
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.finance-comparison-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  padding: 0 0.9rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: #1d4ed8;
}

.finance-comparison-banner button {
  background: none;
  border: none;
  color: #1d4ed8;
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  text-decoration: underline;
}

.finance-kpi-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.finance-bottom-toggle {
  display: flex;
  gap: 0.3rem;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 0.2rem;
  width: fit-content;
  align-self: center;
}

.finance-bottom-toggle__btn {
  background: none;
  border: none;
  border-radius: 6px;
  padding: 0.35rem 0.8rem;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.78rem;
  color: #64748b;
  cursor: pointer;
}

.finance-bottom-toggle__btn--active {
  background: #ffffff;
  color: var(--text-content);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.finance-chart,
.finance-projection {
  flex: 1;
  min-height: 0;
}

.finance-content::-webkit-scrollbar {
  width: 9px;
}

.finance-content::-webkit-scrollbar-thumb {
  background: var(--border-subtle);
  border-radius: 3px;
}

.finance-content::-webkit-scrollbar-track {
  background: transparent;
}

.finance-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-secondary);
}

.finance-loading__spinner {
  font-size: 1.8rem;
  animation: finance-spin 0.8s linear infinite;
}

.finance-loading__text {
  font-size: 0.85rem;
  font-family: var(--font-body);
}

@keyframes finance-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>